import React from "react";
import { graphql } from "react-relay";

import { faGrid2Plus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { Node as ProseNode } from "prosemirror-model";
import { EditorState, NodeSelection } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import { showFeatureModal } from "~/billing";
import type { BillingFeatureAccessType, FeatureValues } from "~/billing/utils";
import { getActionItemKebabMenuEntry } from "~/integrations/actionItemIntegration/actionItemKebabMenuEntry";
import {
	type ActionItemLinkingModalProps,
	displayActionItemLinkingModal,
	type RenderCreateNewExternalTicketPaneType,
	type RenderImportExistingExternalTicketPane,
} from "~/integrations/actionItemIntegration/actionItemLinkingModal";
import { displayActionItemUnlinkingModal } from "~/integrations/actionItemIntegration/actionItemUnlinkingModal";
import { promptConnectingIntegrationModal } from "~/integrations/actionItemIntegration/promptConnectingIntegration";
import { slashCommandWrapper } from "~/integrations/actionItemIntegration/slashCommandWrapper";
import clickup from "~/integrations/clickup";
import hubspotTask from "~/integrations/hubspotTask";
import { linear } from "~/integrations/linear";
import { microsoftTodo } from "~/integrations/microsoftTodo";
import { monday } from "~/integrations/monday";
import trello from "~/integrations/trello";
import type { AuthPostMessage, OAuthParams } from "~/lib/auth";
import {
	type ActionItemIntegrationLinkNode,
	ActionItemIntegrationLinkPluginKey,
} from "~/lib/prose/plugins/actionItemIntegrationLink";
import { getContext } from "~/lib/prose/plugins/context";
import type { SlashCommandChoice } from "~/lib/prose/plugins/slash";
import type { getPosHandler } from "~/lib/prose/react-node-view";
import { fromEntries, type TooltipProps } from "~/lib/utils";
import type { AssignableDropdownItemType, DisableOnLinkedCopyArgs } from "~/notes/AssignableDropdown";
import type { StreamSettingsResponseStream } from "~/stream/StreamSettings";
import type {
	ActionItemIntegrationCardProps,
	IntegrationConfigProps,
	IntegrationDetailsType,
} from "~/stream/StreamSettings/ActionItemIntegrations";

import asana, { asanaKey } from "../asana/index";
import jira, { type JiraIntegrationLinkProps, jiraKey } from "../jira/index";
import type { ActionItemIntegrationKebabModalCardProps } from "./actionItemIntegrationKebabModalCard";
import { displayAllActionItemIntegrationsModal } from "./allActionItemIntegrationsModal";
import { selectionToJiraText } from "./utils";

import type { IntegrationProvider as _IntegrationProvider } from "~/__generated__/actionItemIntegrationDummyQuery.graphql";
import type { StreamSettingsModalQuery } from "~/__generated__/StreamSettingsModalQuery.graphql";

export interface ConfigurationPaneProps {
	stream: NonNullable<StreamSettingsResponseStream>;
	viewer?: StreamSettingsModalQuery["response"]["viewer"];
	onFinish?: () => void;
}

export type IntegrationProvider = _IntegrationProvider;

// Dummy fragment so we can import action item integration types
graphql`
	query actionItemIntegrationDummyQuery {
		viewer {
			id
			connectedActionItemIntegrations
		}
	}
`;

export interface BaseIntegrationDetails {
	containerTitle: string;
	createdBy: string;
	includeAiGenerated: boolean;
}

export interface ActionItemIntegration {
	logo: React.ReactNode;
	key: IntegrationProvider;
	displayName: string;
	externalActionItemName: string;
	billingFeatureName: FeatureValues;
	externalContainerType: string;
	renderPill?: (props: renderPillArgument) => React.ReactElement;
	isActionItemIntegrated?: (arg: isActionItemIntegratedArgument) => boolean;
	getDropdownMenuItem?: (
		arg: assignableDropdownMenuArgument & { actionItemLinkedToIntegration: boolean },
	) => NonNullable<AssignableDropdownItemType>;
	renderCreateNewExternalTicketPane?: RenderCreateNewExternalTicketPaneType;
	renderImportExistingExternalTicketPane?: RenderImportExistingExternalTicketPane;
	promptAuth?: (params: OAuthParams) => Promise<AuthPostMessage>;
	streamSettingsCard?: (props: ActionItemIntegrationCardProps) => JSX.Element;
	kebabModalCard?: (props: Omit<ActionItemIntegrationKebabModalCardProps, "integration">) => JSX.Element;
	configurationPane?: <T extends BaseIntegrationDetails>(props: IntegrationConfigProps<T>) => JSX.Element;
	useIntegrationDetails: (integrationDataOrAsanaStream: IntegrationDetailsType) => BaseIntegrationDetails;
}

export type isActionItemIntegratedArgument = {
	state: EditorState;
	blockKey: string;
	position: number;
};

export type renderPillArgument = {
	actionItemLink: Pick<ActionItemIntegrationLinkNode, "syncStatus" | "integrationData">;
	customClassName?: string;
};

type assignableDropdownMenuArgument = {
	hasAccessToJiraIntegration: boolean;
	hasAccessToAsana: boolean;
	billingFeatureAccess?: BillingFeatureAccessType;
	setBlockAttrs: (values: ProseNode["attrs"]) => unknown;
	view: EditorView;
	getPos: getPosHandler;
	close: () => void;
	jira?: { id: string; clientKey: string; baseUrl: string };
	isAccountAdmin?: boolean;
	blockKey: string;
	noteId: string;
	connectedActionItemIntegrations: IntegrationProvider[];
	disabled: boolean;
	tooltip?: TooltipProps;
} & DisableOnLinkedCopyArgs;

export type ActionItemIntegrationWithPanes = Omit<
	ActionItemIntegration,
	"renderCreateNewExternalTicketPane" | "renderImportExistingExternalTicketPane"
> &
	Required<Pick<ActionItemIntegration, "renderCreateNewExternalTicketPane" | "renderImportExistingExternalTicketPane">>;

export const integrations = [asana, jira, clickup, trello, monday, linear, microsoftTodo, hubspotTask] as const;

export function isActionItemIntegrationWithPanes(
	actionItemIntegration: ActionItemIntegration,
): actionItemIntegration is ActionItemIntegrationWithPanes {
	return !!(
		actionItemIntegration.renderCreateNewExternalTicketPane &&
		actionItemIntegration.renderImportExistingExternalTicketPane
	);
}

export function getIntegrationForKey(key: (typeof integrations)[number]["key"]) {
	for (let integration of integrations) {
		if (integration.key === key) {
			return integration;
		}
	}
	throw new Error("No integration for key");
}

export function getIntegrationStreamSettingsCards() {
	const settingsCards: NonNullable<ActionItemIntegration["streamSettingsCard"]>[] = [];
	for (let integration of integrations) {
		if (integration.streamSettingsCard) {
			settingsCards.push(integration.streamSettingsCard);
		}
	}
	return settingsCards;
}

export function activeIntegrationLinks(actionItemIntegratedArgument: isActionItemIntegratedArgument): {
	[property in IntegrationProvider]: boolean;
} {
	const { state, blockKey } = actionItemIntegratedArgument;

	const currentBlockActionItemIntegration =
		ActionItemIntegrationLinkPluginKey.getState(state)?.integrationLinks[blockKey];

	return fromEntries(
		integrations.map(integration => {
			if (integration.isActionItemIntegrated) {
				return [integration.key, integration.isActionItemIntegrated(actionItemIntegratedArgument)];
			}

			const active =
				!!currentBlockActionItemIntegration &&
				currentBlockActionItemIntegration.integrationProvider === integration.key;

			return [integration.key, active];
		}),
	);
}

export function getActionItemIntegrationMenus(
	arg: assignableDropdownMenuArgument,
): NonNullable<AssignableDropdownItemType>[] {
	let activeIntegrations = new Set(arg.connectedActionItemIntegrations);
	if (getContext(arg.view.state).asanaIsConnected) activeIntegrations.add(asana.key);
	if (arg.jira) activeIntegrations.add(jira.key);

	const { view, getPos, blockKey } = arg;
	const closeMenu = arg.close;

	if (typeof getPos === "boolean") {
		return [];
	}
	const position = getPos();

	const activeActionItemIntegrationLinks = activeIntegrationLinks({
		state: view.state,
		position: position,
		blockKey: blockKey,
	});

	const actionItemLinkActive = Object.values(activeActionItemIntegrationLinks).some(active => active);

	const context = getContext(view.state);
	const { userId, isPrivateNote } = context;
	const selection: NodeSelection = NodeSelection.create(view.state.doc, getPos());
	const initialText: string = selectionToJiraText(selection);

	const displayActionItemLinkingModalProps: Omit<ActionItemLinkingModalProps, "integration"> = {
		initialText: initialText,
		blockKey: blockKey,
		userId: userId,
		noteId: arg.noteId,
	};

	const jiraProps: JiraIntegrationLinkProps = {
		editorState: view.state,
		dispatch: view.dispatch.bind(view),
		selection: NodeSelection.create(view.state.doc, getPos()),
		initialText: initialText,
		isAccountAdmin: arg.isAccountAdmin,
	};

	const menus: NonNullable<AssignableDropdownItemType>[] = [];

	for (let integration of integrations) {
		if (integration.key == "HUBSPOT_TASK") {
			// we don't want to show the hubspot task integration in the dropdown
			continue;
		}

		// Don't display integrations that aren't connected
		if (![asanaKey, jiraKey].includes(integration.key) && !activeIntegrations.has(integration.key)) continue;

		const thisActionItemLinkActive = activeActionItemIntegrationLinks[integration.key];
		// if the action item is linked by a different integration we don't want to display this integration
		if (actionItemLinkActive && !thisActionItemLinkActive) {
			continue;
		}

		if (integration.getDropdownMenuItem) {
			// Only Asana and Jira
			if (integration.key === jiraKey && !arg.jira) continue;
			if (integration.key === asanaKey && !activeIntegrations.has(asanaKey)) continue;
			menus.push(integration.getDropdownMenuItem({ ...arg, actionItemLinkedToIntegration: actionItemLinkActive }));
		} else if (isActionItemIntegrationWithPanes(integration)) {
			menus.push(
				getActionItemKebabMenuEntry({
					blockIdsToNoteIds: {},
					blockKey: blockKey,
					hasAccessToOriginalNote: arg.hasAccessToOriginalNote,
					showUserOrAccountIntegrationInitialization: () => promptConnectingIntegrationModal(integration),
					openDeleteLinkModal: () => {
						displayActionItemUnlinkingModal({
							integration: integration as ActionItemIntegrationWithPanes,
							userId,
							...arg,
						});
						closeMenu();
					},
					openCreateLinkModal: () => {
						displayActionItemLinkingModal({
							integration: integration as ActionItemIntegrationWithPanes,
							...displayActionItemLinkingModalProps,
						});
						closeMenu();
					},
					originalBlockIds: arg.originalBlockIds,
					view: arg.view,
					logo: integration.logo,
					integrationKey: integration.key,
					displayName: integration.displayName,
					actionItemLinkedToIntegration: actionItemLinkActive,
					hasAccessToIntegration: arg.billingFeatureAccess?.[integration.billingFeatureName] || false,
					integrationActive: activeIntegrations.has(integration.key),
					showFeatureModal: showFeatureModal.bind(null, integration.billingFeatureName),
					disabled: arg.disabled,
					tooltip: arg.tooltip,
				}),
			);
		} else {
			throw new Error("ActionItemIntegration must implement getDropdownMenuItem or renderXXXPane methods");
		}
	}

	if (!actionItemLinkActive && !isPrivateNote) {
		menus.push({
			id: "action-item-integration-modal",
			action: (): boolean => {
				displayAllActionItemIntegrationsModal({
					activeIntegrations: activeIntegrations,
					actionItemLinkingModalProps: displayActionItemLinkingModalProps,
					billingFeatureAccess: arg.billingFeatureAccess,
					jiraProps: jiraProps,
				});
				closeMenu();
				return true;
			},
			icon: faGrid2Plus,
			right: (() => {
				return <div>Browse integrations…</div>;
			})(),
			disabled: arg.disabled,
			toolTip: arg.tooltip,
		});
	}

	return menus;
}

export function getSlashCommandEntries(): SlashCommandChoice[] {
	const menus: SlashCommandChoice[] = [];
	for (let integration of integrations) {
		if (isActionItemIntegrationWithPanes(integration)) {
			menus.push({
				title: integration.displayName,
				content: `Import or create a ${integration.externalActionItemName}.`,
				icon: integration.logo,
				// shortcut: `${CtrlOrCmd}+j`,
				replaceMatch: false,
				showInToolbar: true,
				command: slashCommandWrapper(integration),
			});
		}
	}

	const asana = getIntegrationForKey(asanaKey);
	menus.push({
		title: asana.displayName,
		content: `Create an ${asana.displayName} ${asana.externalActionItemName}.`,
		icon: asana.logo,
		replaceMatch: false,
		showInToolbar: true,
		command: slashCommandWrapper(asana),
	});
	return menus;
}
